/**
 * Anime picture-tier vignettes (AUTHORING.md v3): one painted night background per picture
 * (public/vignettes/anime/<bg>.webp, prompts docs/vignette-prompts.py, CREDITS.md) + the picture's posed friend from core
 * (`content/worlds/anime/pictures.ts`). `elements` = every part the painting shows (each one is drawn + coloured in the
 * picture lesson: night sky bands, glow halos, reflections included).
 */
import type { VignetteSpec, WorldVignettes } from '../../vignette/types';

const v = (id: string, elements: string[], overlay?: string[]): VignetteSpec => ({ id: `${id}-picture`, bg: id, elements, ...(overlay ? { overlay } : {}) });

const specs: VignetteSpec[] = [
  /* L1 · Lantern Alley */
  v('anime-chibi-face', ['sky', 'glow', 'ground', 'moon', 'house', 'lanterns']),
  v('anime-sparkly-eyes', ['sky', 'glow', 'ground', 'wall', 'window', 'moon', 'bushes', 'lanterns']),
  v('anime-remix-chibi-face-teary', ['sky', 'glow', 'ground', 'puddles', 'lamps', 'moon', 'houses', 'rain']),
  v('anime-remix-chibi-face-puffed', ['sky', 'glow', 'ground', 'moon', 'stall', 'lanterns']),
  v('anime-remix-chibi-face-wink', ['sky', 'glow', 'ground', 'moon', 'stars', 'tree', 'shooting']),
  /* L2 · Neon Rooftops */
  v('anime-cat-mascot', ['sky', 'glow', 'ground', 'moon', 'chimney', 'laundry']),
  v('anime-chibi-body', ['sky', 'glow', 'ground', 'moon', 'stars', 'houses', 'antenna', 'neon']),
  v('anime-cloud-bunny', ['sky', 'glow', 'ground', 'moon', 'stars', 'house', 'tower', 'oilLamps']),
  v('anime-remix-chibi-body-raincoat', ['sky', 'glow', 'ground', 'puddles', 'moon', 'house', 'pipe', 'oilLamps', 'rain']),
  v('anime-remix-chibi-body-bear-hoodie', ['sky', 'glow', 'ground', 'moon', 'house', 'tent', 'fairy', 'oilLamps']),
  v('anime-remix-chibi-body-star-cape', ['sky', 'glow', 'ground', 'moon', 'pots', 'lanterns']),
  /* L3 · Star Tower */
  v('anime-magic-wand', ['sky', 'glow', 'ground', 'moon', 'milky', 'towerWall', 'towerWall2', 'telescope', 'rail', 'oilLamps']),
  v('anime-flowing-hair', ['sky', 'glow', 'ground', 'moon', 'stars', 'towerWall', 'oilLamps']),
  v('anime-starry-jellyfish', ['sky', 'glow', 'roofs', 'moon', 'clouds', 'house', 'spire', 'oilLamps']),
  v('anime-remix-wand-moon', ['sky', 'ground', 'moon', 'stars', 'dome']),
  v('anime-remix-wand-heart', ['sky', 'glow', 'ground', 'moon', 'lamps', 'bushes']),
  v('anime-remix-wand-snowflake', ['sky', 'ground', 'moon', 'pines', 'oilLamps', 'snowfall']),
  v('anime-remix-hair-twin-tails', ['sky', 'ground', 'moon', 'stars', 'oilLamps', 'kites']),
  v('anime-remix-hair-fluffy-short', ['sky', 'ground', 'puddles', 'moon', 'moonbow', 'oilLamps']),
  v('anime-remix-hair-long-braid', ['sky', 'glow', 'pond', 'moon', 'reeds', 'floaters']),
  /* L4 · Moon Bridge */
  v('anime-ninja', ['sky', 'glow', 'ground', 'moon', 'tree', 'stoneLamp', 'petals']),
  v('anime-dragon', ['sky', 'glow', 'river', 'ground', 'moon', 'reeds', 'oilLamps']),
  v('anime-hoverboard-chibi', ['sky', 'glow', 'river', 'moon', 'houses', 'lanterns']),
  v('anime-remix-dragon-sleepy', ['sky', 'ground', 'moon', 'arch', 'oilLamps']),
  v('anime-remix-dragon-bubbles', ['sky', 'glow', 'ground', 'moon', 'cart', 'lanterns']),
  v('anime-remix-dragon-butterfly-wings', ['sky', 'glow', 'roofs', 'moon', 'clouds']),
  v('anime-remix-ninja-cat', ['sky', 'glow', 'ground', 'moon', 'oilLamps', 'bushes']),
  v('anime-remix-ninja-branch', ['sky', 'glow', 'ground', 'moon', 'tree', 'oilLamps', 'petals']),
  v('anime-remix-ninja-lantern', ['sky', 'glow', 'river', 'ground', 'moon', 'bridge', 'oilLamps']),
  /* playground-only */
  v('anime-star-badge', ['sky', 'glow', 'ground', 'moon', 'gate', 'lanterns']),
];

export const ANIME_VIGNETTES: WorldVignettes = { world: 'anime', specs };
